import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Divider,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  Error as ErrorIcon,
  Warning,
  Info as InfoIcon,
  CheckCircle,
  Block,
} from '@mui/icons-material';
import { Finding } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { analysisService } from '../../services/analysisService';

interface FindingsListProps {
  findings: Finding[] | any[]; // Allow any[] to handle backend format
  deckId?: string;
  slideNumber?: number;
  onAddressedChange?: () => void;
}

export const FindingsList: React.FC<FindingsListProps> = ({ findings, deckId, slideNumber, onAddressedChange }) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [addressedIds, setAddressedIds] = useState<Set<string>>(new Set());
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set());
  const { token } = useAuth();

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getFindingId = (finding: any): string => {
    // Prefer backend-derived key parts when present to ensure exact match server-side
    if (Array.isArray((finding as any).__keyParts)) {
      return (finding as any).__keyParts.join('||');
    }
    // Fallback: compose from visible fields
    return [
      finding.title || '',
      finding.description || '',
      finding.evidence || '',
      finding.recommendation || finding.suggestedFix || '',
      finding.severity || '',
      finding.category || '',
    ].join('||');
  };

  const isAddressed = (f: any): boolean => addressedIds.has(getFindingId(f));
  const isIgnored = (f: any): boolean => ignoredIds.has(getFindingId(f));

  const toggleAddressed = async (f: any) => {
    const id = getFindingId(f);
    // Optimistic update
    setAddressedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Make mutually exclusive with ignored
    setIgnoredIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    try {
      if (deckId && slideNumber && token) {
        await analysisService.setFindingAddressed(deckId, slideNumber, id, !isAddressed(f), token);
        // Avoid triggering parent refetch to prevent full-page refresh effect
      }
    } catch (e) {
      // Keep optimistic UI; optionally log
      // console.warn('Failed to persist addressed state', e);
    }
  };

  const toggleIgnored = async (f: any) => {
    const id = getFindingId(f);
    // Optimistic update
    setIgnoredIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    // Make mutually exclusive with addressed
    setAddressedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    try {
      if (deckId && slideNumber && token) {
        await analysisService.setFindingIgnored(deckId, slideNumber, id, !isIgnored(f), token);
        // Avoid triggering parent refetch to prevent full-page refresh effect
      }
    } catch (e) {
      // Keep optimistic UI; optionally log
      // console.warn('Failed to persist ignored state', e);
    }
  };

  // Map backend fields to frontend fields
  const normalizeFinding = (finding: any): Finding => {
    const keyParts = [
      finding.title || finding.issue || finding.type || '',
      finding.description || finding.issue || '',
      finding.evidence || finding.location || '',
      finding.recommendation || finding.suggestedFix || finding.suggestion || '',
      finding.severity || '',
      finding.category || finding.type || '',
    ];
    const normalized: any = {
      title: finding.title || finding.issue || finding.type || 'Issue',
      description: finding.description || finding.issue || '',
      severity: finding.severity || 'low',
      category: finding.category || finding.type || 'General',
      evidence: finding.evidence || finding.location || undefined,
      recommendation: finding.recommendation || finding.suggestion || finding.suggestedFix || undefined,
      suggestedFix: finding.suggestedFix || finding.suggestion || undefined,
      impact: finding.impact || undefined,
      // preserve persistence flags from backend
      addressed: finding.addressed,
      ignored: finding.ignored,
    };
    // attach backend-derived key parts for stable id
    normalized.__keyParts = keyParts;
    return normalized as Finding;
  };

  // Map backend severity names to frontend names
  const normalizeSeverity = (severity: string): 'critical' | 'high' | 'low' => {
    const normalized = severity.toLowerCase();
    if (normalized === 'major') return 'high';
    if (normalized === 'minor') return 'low';
    if (normalized === 'critical') return 'critical';
    if (normalized === 'high') return 'high';
    if (normalized === 'low') return 'low';
    return 'low'; // default
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' => {
    const normalized = normalizeSeverity(severity);
    switch (normalized) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'info';
    }
  };

  const getSeverityIcon = (severity: string) => {
    const normalized = normalizeSeverity(severity);
    switch (normalized) {
      case 'critical':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'high':
        return <Warning sx={{ color: 'warning.main' }} />;
      case 'low':
        return <InfoIcon sx={{ color: 'info.main' }} />;
      default:
        return <InfoIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getCategoryColor = (category: string): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' => {
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('content')) return 'primary';
    if (categoryLower.includes('design') || categoryLower.includes('visual')) return 'secondary';
    if (categoryLower.includes('accessibility')) return 'info';
    if (categoryLower.includes('readability')) return 'warning';
    return 'default';
  };

  // Normalize findings and group by severity
  const normalizedFindings = useMemo(() => (findings || []).map(normalizeFinding), [findings]);
  // Initialize addressed/ignored sets from incoming findings (persisted)
  React.useEffect(() => {
    const incomingAddressed = new Set<string>();
    const incomingIgnored = new Set<string>();
    normalizedFindings.forEach((f) => {
      const id = getFindingId(f);
      if ((f as any).addressed) incomingAddressed.add(id);
      if ((f as any).ignored) incomingIgnored.add(id);
    });
    setAddressedIds(incomingAddressed);
    setIgnoredIds(incomingIgnored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedFindings]);
  const groupedFindings = useMemo(() => {
    return normalizedFindings.reduce((acc, finding) => {
      const severity = normalizeSeverity(finding.severity || 'low');
      if (!acc[severity]) {
        acc[severity] = [];
      }
      acc[severity].push(finding);
      return acc;
    }, {} as Record<string, Finding[]>);
  }, [normalizedFindings]);

  const severityOrder = ['critical', 'high', 'low'];

  return (
    <Box>
      {normalizedFindings.length === 0 ? (
        <Alert severity="success" icon={<CheckCircle />}>
          <Typography variant="body1" fontWeight={600}>
            No Issues Found
          </Typography>
          <Typography variant="body2">
            This slide looks great! No issues were detected during analysis.
          </Typography>
        </Alert>
      ) : null}

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {severityOrder.map((severity) => {
          const count = groupedFindings[severity]?.length || 0;
          if (count === 0) return null;
          return (
            <Chip
              key={severity}
              label={`${count} ${severity.charAt(0).toUpperCase() + severity.slice(1)}`}
              color={getSeverityColor(severity)}
              size="small"
              variant={severity === 'critical' || severity === 'high' ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>

      {(() => {
        // Render unaddressed/unignored items in severity groups first
        const addressedPool: Array<{ finding: Finding; severity: string; index: number }> = [];
        const ignoredPool: Array<{ finding: Finding; severity: string; index: number }> = [];

        return (
          <>
            {severityOrder.map((severity) => {
              const severityFindings = groupedFindings[severity];
              if (!severityFindings || severityFindings.length === 0) return null;

              const unaddressedUnignored = severityFindings.filter((f) => !isAddressed(f) && !isIgnored(f));
              const addressed = severityFindings.filter((f) => isAddressed(f));
              const ignored = severityFindings.filter((f) => isIgnored(f));
              addressed.forEach((f, idx) => addressedPool.push({ finding: f, severity, index: idx }));
              ignored.forEach((f, idx) => ignoredPool.push({ finding: f, severity, index: idx }));

              if (unaddressedUnignored.length === 0) return null;

              return (
                <Box key={severity} mb={2}>
                  {unaddressedUnignored.map((finding, index) => {
                    const panelId = `${severity}-u-${index}`;
                    const color: 'error' | 'warning' | 'info' | 'success' = getSeverityColor(severity);
                    return (
                      <Accordion
                        key={panelId}
                        expanded={expanded === panelId}
                        onChange={handleChange(panelId)}
                        sx={{
                          mb: 1,
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            '& .MuiAccordionSummary-content': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            },
                          }}
                        >
                          {getSeverityIcon(severity)}
                          <Box flexGrow={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {finding.title}
                            </Typography>
                            <Box display="flex" gap={1} mt={0.5}>
                              {finding.category && (
                                <Chip
                                  label={finding.category}
                                  size="small"
                                  color={getCategoryColor(finding.category)}
                                  variant="outlined"
                                />
                              )}
                              <Chip label={severity.toUpperCase()} size="small" color={color} />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box>
                            {finding.description && (
                              <Box mb={2}>
                                <Typography variant="body2" color="text.secondary">
                                  {finding.description}
                                </Typography>
                              </Box>
                            )}

                            {finding.evidence && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Evidence
                                </Typography>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                  <Typography variant="body2">{finding.evidence}</Typography>
                                </Alert>
                              </Box>
                            )}

                            {finding.impact && (
                              <Box mb={2}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Impact
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {finding.impact}
                                </Typography>
                              </Box>
                            )}

                            {(finding.recommendation || finding.suggestedFix) && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.main">
                                    Recommended Action
                                  </Typography>
                                  <Alert severity="success" sx={{ py: 0.5 }}>
                                    <Typography variant="body2">
                                      {finding.recommendation || finding.suggestedFix}
                                    </Typography>
                                  </Alert>
                                </Box>
                              </>
                            )}

                            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                              <Button variant="outlined" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleIgnored(finding); }}>
                                Ignore
                              </Button>
                              <Button variant="contained" color="success" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAddressed(finding); }}>
                                Mark Addressed
                              </Button>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              );
            })}

            {/* Addressed items rendered last */}
            {addressedPool.length > 0 && (
              <Box mb={2}>
                {addressedPool.map(({ finding, severity }, index) => {
                  const panelId = `addressed-${index}`;
                  const color: 'success' = 'success';
                  return (
                    <Accordion
                      key={panelId}
                      expanded={expanded === panelId}
                      onChange={handleChange(panelId)}
                      sx={{
                        mb: 1,
                        '&:before': { display: 'none' },
                        bgcolor: 'rgba(76, 175, 80, 0.04)',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          },
                        }}
                      >
                        <CheckCircle sx={{ color: 'success.main' }} />
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {finding.title}
                          </Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            {finding.category && (
                              <Chip
                                label={finding.category}
                                size="small"
                                color={getCategoryColor(finding.category)}
                                variant="outlined"
                              />
                            )}
                            <Chip label={severity.toUpperCase()} size="small" variant="outlined" />
                            <Chip label="Addressed" size="small" color={color} />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          {finding.description && (
                            <Box mb={2}>
                              <Typography variant="body2" color="text.secondary">
                                {finding.description}
                              </Typography>
                            </Box>
                          )}

                          {finding.evidence && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Evidence
                              </Typography>
                              <Alert severity="info" sx={{ py: 0.5 }}>
                                <Typography variant="body2">{finding.evidence}</Typography>
                              </Alert>
                            </Box>
                          )}

                          {finding.impact && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Impact
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {finding.impact}
                              </Typography>
                            </Box>
                          )}

                          {(finding.recommendation || finding.suggestedFix) && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.main">
                                  Recommended Action
                                </Typography>
                                <Alert severity="success" sx={{ py: 0.5 }}>
                                  <Typography variant="body2">
                                    {finding.recommendation || finding.suggestedFix}
                                  </Typography>
                                </Alert>
                              </Box>
                            </>
                          )}

                          <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button variant="outlined" color="success" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAddressed(finding); }}>
                              Unmark Addressed
                            </Button>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}

            {/* Ignored items rendered last (after addressed) */}
            {ignoredPool.length > 0 && (
              <Box mb={2}>
                {ignoredPool.map(({ finding, severity }, index) => {
                  const panelId = `ignored-${index}`;
                  return (
                    <Accordion
                      key={panelId}
                      expanded={expanded === panelId}
                      onChange={handleChange(panelId)}
                      sx={{
                        mb: 1,
                        '&:before': { display: 'none' },
                        bgcolor: 'action.hover',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          },
                        }}
                      >
                        <Block sx={{ color: 'text.disabled' }} />
                        <Box flexGrow={1}>
                          <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                            {finding.title}
                          </Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            {finding.category && (
                              <Chip
                                label={finding.category}
                                size="small"
                                color={getCategoryColor(finding.category)}
                                variant="outlined"
                              />
                            )}
                            <Chip label={severity.toUpperCase()} size="small" variant="outlined" />
                            <Chip label="Ignored" size="small" />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          {finding.description && (
                            <Box mb={2}>
                              <Typography variant="body2" color="text.secondary">
                                {finding.description}
                              </Typography>
                            </Box>
                          )}

                          {finding.evidence && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Evidence
                              </Typography>
                              <Alert severity="info" sx={{ py: 0.5 }}>
                                <Typography variant="body2">{finding.evidence}</Typography>
                              </Alert>
                            </Box>
                          )}

                          {finding.impact && (
                            <Box mb={2}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Impact
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {finding.impact}
                              </Typography>
                            </Box>
                          )}

                          {(finding.recommendation || finding.suggestedFix) && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                  Recommended Action
                                </Typography>
                                <Alert severity="success" sx={{ py: 0.5 }}>
                                  <Typography variant="body2">
                                    {finding.recommendation || finding.suggestedFix}
                                  </Typography>
                                </Alert>
                              </Box>
                            </>
                          )}

                          <Box mt={2} display="flex" justifyContent="flex-end">
                            <Button variant="outlined" size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleIgnored(finding); }}>
                              Unignore
                            </Button>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </>
        );
      })()}
    </Box>
  );
};
